// ---------------------------------------------------------------------------
// ProductSearchService
//
// Wraps productInventoryService and the mapping logic that used to be three
// separate free functions inside PlanogramDetails.tsx: safeString,
// getResponseItems, getParsedValue, mapProductToPlanogramOption, and
// getProductDetailsByExternalId.
//
// This is a plain class (not a hook) because it has no React state of its
// own — it's a stateless façade over the HTTP/service layer. The "latest
// request wins" debounce-guard logic (latestProductSearchRef in the
// original) DOES involve state, so that part stays in the hook
// (useProductSearch) that wraps this service, not here.
// ---------------------------------------------------------------------------

import { productInventoryService } from '../../../../service/warehouse';
import { ProductOption } from '../domain/types';

const safeString = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const getParsedValue = (value: unknown): unknown => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
        const v = value as { parsedValue?: unknown; source?: unknown };
        return v.parsedValue ?? v.source ?? null;
    }
    return value;
};

const getResponseItems = (response: unknown): unknown[] => {
    const r = response as Record<string, unknown> | null;
    return (r?.data ?? r?.content ?? []) as unknown[];
};

export class ProductSearchService {
    static mapProductToOption(
        product: Record<string, unknown>,
    ): ProductOption | null {
        const externalId = safeString(
            product.externalId ??
            product.external_id ??
            product.uuid ??
            product.id,
        );

        const systemId = safeString(
            product.systemId ??
            product.system_id,
        );

        const submitId =
            externalId || systemId;

        if (!submitId) {
            return null;
        }

        const structuredWeightOrVolume =
            (
                product.weightOrVolume ??
                product.weight_or_volume
            ) as
                | Record<string, unknown>
                | null
                | undefined;

        const rawWeightVolume =
            structuredWeightOrVolume?.value ??
            product.weightVolume ??
            product.weight_volume ??
            null;

        const parsedWeightVolume =
            getParsedValue(
                rawWeightVolume,
            );

        const normalizedWeightVolume =
            typeof parsedWeightVolume ===
                'number'
                ? Number.isFinite(
                    parsedWeightVolume,
                )
                    ? parsedWeightVolume
                    : null
                : typeof parsedWeightVolume ===
                    'string'
                    ? (() => {
                        const normalized =
                            parsedWeightVolume
                                .trim()
                                .replace(',', '.');

                        if (!normalized) {
                            return null;
                        }

                        const parsed =
                            Number(normalized);

                        return Number.isFinite(
                            parsed,
                        )
                            ? parsed
                            : null;
                    })()
                    : null;

        const rawUnit =
            structuredWeightOrVolume?.unit ??
            product.unitOfMeasurement ??
            product.unit_of_measurement ??
            null;

        const getUnitValue = (
            value: unknown,
        ): string => {
            if (
                value === null ||
                value === undefined
            ) {
                return '';
            }

            if (
                typeof value === 'string' ||
                typeof value === 'number'
            ) {
                return String(value).trim();
            }

            if (typeof value === 'object') {
                const option =
                    value as Record<
                        string,
                        unknown
                    >;

                return safeString(
                    option.value ??
                    option.id ??
                    option.code ??
                    option.name ??
                    option.label ??
                    option.parsedValue ??
                    option.source,
                ).trim();
            }

            return '';
        };

        const unit =
            getUnitValue(rawUnit);

        const weightOrVolume =
            normalizedWeightVolume !== null &&
            unit
                ? {
                    value:
                        normalizedWeightVolume,
                    unit,
                }
                : null;

        const productName =
            safeString(
                product.name ??
                product.productName ??
                product.product_name,
            );

        const label = [
            productName,

            weightOrVolume
                ? `${weightOrVolume.value} ${weightOrVolume.unit}`
                : null,

            product.ean
                ? `EAN: ${safeString(product.ean)}`
                : null,
        ]
            .filter(Boolean)
            .join(' / ');

        const rawCurrency =
            product.currency as
                | Record<string, unknown>
                | string
                | null
                | undefined;

        const currency =
            rawCurrency &&
            typeof rawCurrency === 'object'
                ? rawCurrency.value ??
                    rawCurrency.id ??
                    rawCurrency.code ??
                    null
                : rawCurrency ?? null;

        const rawCategory =
            product.category as
                | Record<string, unknown>
                | null
                | undefined;

        const categoryName =
            rawCategory?.name ??
            rawCategory?.label ??
            product.categoryName ??
            product.category_name ??
            null;

        return {
            value:
                submitId,
            id:
                submitId,
            externalId,
            systemId,
            externalSystemId:
                safeString(
                    product.externalSystemId ??
                    product.external_system_id,
                ),

            label,
            name:
                productName,
            productName,

            ean:
                safeString(product.ean),

            price:
                getParsedValue(
                    product.price,
                ),

            currency,

            minQuantity:
                (
                    product.minQuantity ??
                    product.min_quantity ??
                    0
                ) as number,

            maxQuantity:
                (
                    product.maxQuantity ??
                    product.max_quantity ??
                    0
                ) as number,

            /*
            * Structured representation used by the planogram API.
            */
            weightOrVolume,

            /*
            * Legacy values used by the tabular editor.
            */
            weightVolume:
                weightOrVolume?.value ??
                normalizedWeightVolume,

            unitOfMeasurement:
                weightOrVolume?.unit ??
                unit ??
                null,

            categoryName:
                categoryName as string | null,

            categoryColor:
                (
                    product.categoryColor ??
                    product.category_color ??
                    '#C0C0C0'
                ) as string,

            hasPhoto:
                Boolean(
                    product.hasPhoto ??
                    product.has_photo ??
                    false,
                ),

            photoUrl:
                (
                    product.photoUrl ??
                    product.photo_url ??
                    product.productPhotoUrl ??
                    null
                ) as string | null,

            raw:
                product,
        };
    }
    static async search(query: string, max = 20): Promise<ProductOption[]> {
        const trimmed = query.trim();
        if (trimmed.length < 2) return [];

        const response = await productInventoryService.list({
            first: 0,
            max,
            search: trimmed,
            sort_field: 'name',
            sort_asc: true,
            only_active: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        return getResponseItems(response)
            .map(item => this.mapProductToOption(item as Record<string, unknown>))
            .filter((x): x is ProductOption => x !== null);
    }

    static async getDetailsByExternalId(externalId: string): Promise<Record<string, unknown> | null> {
        if (!externalId) return null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (typeof (productInventoryService as any).get === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (productInventoryService as any).get(externalId) as Promise<Record<string, unknown>>;
        }

        const response = await fetch(`/portal/inventory/product/${externalId}`);
        if (!response.ok) {
            throw new Error('Failed to fetch product details');
        }
        return response.json() as Promise<Record<string, unknown>>;
    }
}